import React, { useRef, useCallback, useContext, useMemo } from 'react';
import { ThemeProvider } from 'styled-components';
import { CheckboxProps, CheckboxOwnProps } from './types';
import { Container, Input, Label, Span } from './styles';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import BasicIcon from '../BasicIcon/BasicIcon';
import { StatusButtonType } from '../ThemeProvider/types';
/**
 * Checkbox.
 *
 * Usage:
 * ```
 * <Checkbox
 *  label=Name to especify the value once is checked
 *  input= object from React Final Form
 *  value= indicates if it's ckecked or not
 *  meta= object from React Final Form
 * />
 * ```
 
 */
const Checkbox: React.FC<CheckboxProps | CheckboxOwnProps> = (props) => {
  const {
    id = 'checkbox',
    label,
    input,
    value,
    onChange,
    testId,
    disabled,
    size = 'medium',
    status = 'default',
  } = props;
  const { actualTheme } = useContext(ThemeContext);
  const checked = useMemo(() => input?.value || value || false, [value, input?.value]);
  const inputRef: React.Ref<HTMLInputElement> = useRef(null);

  const handleChange = useCallback(() => {
    if (onChange) onChange(!checked);
    if (input) input.onChange(!checked);
  }, [checked, input, onChange]);

  const handleSpan = useCallback(
    (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.stopPropagation();
      inputRef.current.click();
    },
    [inputRef],
  );

  const iconSize = useMemo(() => {
    if (size === 'small') return 'extra-small';
    if (size === 'medium') return 'small';
    return 'medium';
  }, [size]);

  const handleClick = useCallback((e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
  }, []);

  return (
    <ThemeProvider theme={actualTheme}>
      <Container id="checkbox-id" size={size}>
        <Input
          {...input}
          ref={inputRef}
          checked={checked}
          id={id}
          type="checkbox"
          onChange={handleChange}
          onClick={handleClick}
          disabled={disabled}
          status={status}
          data-testid={testId}
        />
        <Span onClick={handleSpan} role="presentation" size={size}>
          <BasicIcon
            name="check"
            size={iconSize}
            clickable
            color={
              !disabled
                ? actualTheme.status[status as StatusButtonType].contrast
                : actualTheme.white
            }
          />
        </Span>
        {label && (
          <Label data-testid={`${testId}-label`} htmlFor={id}>
            {label}
          </Label>
        )}
      </Container>
    </ThemeProvider>
  );
};

export default Checkbox;
